import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FileModel, FolderModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useProjectSnapshot } from '../../../hooks/use-project-snapshot';
import { Button } from '../../button/button';

interface Props {
  files: FileModel[];
  folders: FolderModel[];
  disabled: boolean;
}

/**
 * Button for creating a batch download for all specified folders and files.
 * During request button gets disabled with spinning loader icon.
 *
 * If request is successful, toast is displayed with information about batch process started.
 * If request fails, toast is displayed.
 */
const BatchDownloadButton = (props: Props) => {
  const { project } = useProjectSnapshot();
  const { t } = useTranslation();
  const [pending, setPending] = React.useState(false);

  const onClick = async () => {
    try {
      setPending(true);

      const response = await SkyMapAxiosServiceFactory.instance
        .createFileServiceV1()
        .createBatchDownload({
          body: {
            folderIds: props.folders.map((x) => x.id),
            fileIds: props.files.map((x) => x.id),
          },
        });
      publish(SubscriptionTopic.WebsocketMessageRecieved, {
        message: 'BatchDownloadUpdated',
        status: 'RUNNING',
        batchDownloadId: response.data.id,
        fileName: `${project!.name}_${format(Date.now(), 'yyyyMMdd_HHmm')}.zip`,
        progress: 0,
        projectName: project!.name,
      });
    } catch (err) {
      publish(SubscriptionTopic.ToastrMessage, {
        message: t('fileManager.actionBar.download.unexpectedError', { ns: 'components' }),
        type: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <Button
      disabled={props.disabled || pending}
      leftIcon={{ icon: ['fad', pending ? 'spinner' : 'download'], spin: pending }}
      variant="text"
      onClick={onClick}
    >
      {t('fileManager.actionBar.download.text', { ns: 'components' })}
    </Button>
  );
};

export { BatchDownloadButton };
